import React, { useMemo, useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, EyeIcon, DownloadIcon, FilterIcon, CreditCardIcon, QrCodeIcon, SmartphoneIcon, FileTextIcon, TerminalIcon, RefreshCwIcon, MonitorIcon, XIcon, MoreVerticalIcon } from 'lucide-react';
import { Sale, TransactionSource, TransactionStatus } from '../../../shared/types/Transaction';
import { RefundModal } from '../../refunds/components/RefundModal';
import { buildMockSales } from '../../../shared/mocks/mockSales';
import { formatMoney } from '../../../shared/lib/money';
export function Sales() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedSaleForRefund, setSelectedSaleForRefund] = useState<Sale | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Use shared mock sales fixtures (single source of truth)
  const sales: Sale[] = useMemo(() => buildMockSales(), []);
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSourceFilter('all');
    setPaymentMethodFilter('all');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
  };
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || sourceFilter !== 'all' || paymentMethodFilter !== 'all' || dateFrom !== '' || dateTo !== '' || amountMin !== '' || amountMax !== '';
  const getSourceIcon = (source: TransactionSource) => {
    const icons = {
      payment_link: <CreditCardIcon size={16} data-id="element-12650" data-mp="2e1o50oy41k5" />,
      qr_code: <QrCodeIcon size={16} data-id="element-12651" data-mp="2bp0q80f4yzp" />,
      tap_to_phone: <SmartphoneIcon size={16} data-id="element-12652" data-mp="3uhcehgg8hhsd" />,
      invoice: <FileTextIcon size={16} data-id="element-12653" data-mp="3ugarcdly14ua" />,
      pos: <TerminalIcon size={16} data-id="element-12654" data-mp="cbpny51erz96" />,
      recurring: <RefreshCwIcon size={16} data-id="element-12655" data-mp="cao7tm5jcmkl" />,
      virtual_terminal: <MonitorIcon size={16} data-id="element-12656" data-mp="7d5y3tjp23bj" />
    };
    return icons[source];
  };
  const getSourceLabel = (source: TransactionSource) => {
    const labels = {
      payment_link: 'Payment Link',
      qr_code: 'QR Code',
      tap_to_phone: 'Tap to Phone',
      invoice: 'Invoice',
      pos: 'POS Terminal',
      recurring: 'Recurring',
      virtual_terminal: 'Virtual Terminal'
    };
    return labels[source];
  };
  const getPaymentMethodCategory = (paymentMethod: Sale['paymentMethod']) => {
    const categories: Record<string, string> = {
      card: 'Card',
      wallet: 'E-wallet',
      bank_transfer: 'Bank transfer',
      cash: 'Cash'
    };
    return categories[paymentMethod.type] || paymentMethod.type;
  };
  const getPaymentMethodName = (paymentMethod: Sale['paymentMethod']) => {
    if (paymentMethod.brand) return paymentMethod.brand;
    if (paymentMethod.details) return paymentMethod.details;
    const fallback: Record<string, string> = {
      card: 'Card',
      wallet: 'E-wallet',
      bank_transfer: 'Bank transfer',
      cash: 'Cash'
    };
    return fallback[paymentMethod.type] || paymentMethod.type;
  };
  const getStatusBadge = (status: TransactionStatus) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      partially_refunded: 'bg-emerald-100 text-emerald-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-12657" data-mp="4xflmevu2nnq">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || sale.source === sourceFilter;
    const matchesPaymentMethod = paymentMethodFilter === 'all' || sale.paymentMethod.type === paymentMethodFilter;
    const saleDate = new Date(sale.date);
    const matchesDateFrom = !dateFrom || saleDate >= new Date(dateFrom);
    const matchesDateTo = !dateTo || saleDate <= new Date(dateTo + 'T23:59:59');
    const matchesAmountMin = !amountMin || sale.amount >= parseFloat(amountMin);
    const matchesAmountMax = !amountMax || sale.amount <= parseFloat(amountMax);
    return matchesSearch && matchesStatus && matchesSource && matchesPaymentMethod && matchesDateFrom && matchesDateTo && matchesAmountMin && matchesAmountMax;
  });
  const totalVolume = filteredSales.reduce((sum, sale) => sum + sale.amount, 0);
  const totalTransactions = filteredSales.length;
  const successfulTransactions = filteredSales.filter(s => s.status === 'completed').length;
  const successRate = totalTransactions > 0 ? Math.round(successfulTransactions / totalTransactions * 100) : 0;
  // Derive currency from first sale (assume single-currency list)
  const currency = filteredSales[0]?.currency || getTenant().currency.code;
  const handleRefund = (amount: number, reason: string, notes: string) => {
    console.log('Refund initiated:', {
      transactionId: selectedSaleForRefund?.transactionId,
      amount,
      reason,
      notes
    });
    // In a real app, this would call an API to process the refund
    // For now, we'll just close the modal and show a success message
    alert(`Refund of ${formatMoney(amount, selectedSaleForRefund?.currency)} initiated successfully!`);
  };
  const handleOpenRefundModal = (sale: Sale) => {
    setSelectedSaleForRefund(sale);
    setRefundModalOpen(true);
  };
  return <div className="space-y-6" data-id="element-12658" data-mp="139nn3sxq09ho">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-12659" data-mp="1hxsb9cfjld7u">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12660" data-mp="1d60kp1wdz7hp">
          <div className="text-sm font-medium text-gray-500" data-id="element-12661" data-mp="1d3xiljhylnwm">
            {t('pages.Sales.total_volume')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-12662" data-mp="khct6ol1nlvp">
            {formatMoney(totalVolume, currency)}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12663" data-mp="2w3wscf87jk7d">
          <div className="text-sm font-medium text-gray-500" data-id="element-12664" data-mp="30vydyrr9mna2">
            {t('pages.Sales.total_transactions')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-12665" data-mp="2n5bakmak897n">
            {totalTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12666" data-mp="2r3iqxg1438ib">
          <div className="text-sm font-medium text-gray-500" data-id="element-12667" data-mp="3evpm4bv3p9g5">
            {t('pages.Sales.successful')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-12668" data-mp="3js6j7vka2y5d">
            {successfulTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-12669" data-mp="3at304rl42iqv">
          <div className="text-sm font-medium text-gray-500" data-id="element-12670" data-mp="3avfhb2j1op2v">
            {t('pages.Sales.success_rate')}
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-12671" data-mp="1zeyw0skt7e83">
            {successRate}%
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-12672" data-mp="2q2emuu7a21yq">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-12673" data-mp="2m1mc1wirbp0c">
          <div className="space-y-4" data-id="element-12674" data-mp="2h31btx3i95bw">
            {/* Primary Filters Row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-12675" data-mp="2c4ztjedwla1h">
              <div className="flex-1 max-w-lg" data-id="element-12676" data-mp="39s8l9r4hzfbx">
                <div className="relative" data-id="element-12677" data-mp="39rysxwvzcw32">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-12678" data-mp="34tf85zt4hqca" />

                  <input type="text" placeholder={t('pages.Sales.attrs.search_by_transaction_id_customer_name')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-12679" data-mp="300lz4zqmfvue" />
                </div>
              </div>
              <div className="flex items-center gap-3" data-id="element-12680" data-mp="8w7jlmr4yyh2">
                <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${showAdvancedFilters ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`} data-id="element-12681" data-mp="8veib1ay516b">
                  <FilterIcon size={18} data-id="element-12682" data-mp="munza36z7zbc" />
                  {t('pages.Sales.filters')}
                  {hasActiveFilters && <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full" data-id="element-12683" data-mp="ros7sqvw4fej">
                      {[searchTerm, statusFilter !== 'all', sourceFilter !== 'all', paymentMethodFilter !== 'all', dateFrom, dateTo, amountMin, amountMax].filter(Boolean).length}
                    </span>}
                </button>
                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-12684" data-mp="hjtacf4az4jv">
                  <DownloadIcon size={18} data-id="element-12685" data-mp="mhcozg4wxjcc" />
                  {t('pages.Sales.export')}
                </button>
              </div>
            </div>
            {/* Advanced Filters Panel */}
            {showAdvancedFilters && <div className="p-4 bg-gray-50 rounded-lg space-y-4" data-id="element-12686" data-mp="vjvmdkpxj3r7">
                <div className="flex items-center justify-between" data-id="element-12687" data-mp="10h49lzpxpxzb">
                  <h3 className="text-sm font-medium text-gray-700" data-id="element-12688" data-mp="2xap9qu65sp9r">
                    {t('pages.Sales.advanced_filters')}
                  </h3>
                  {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-12689" data-mp="2uq13p987bqdd">
                      <XIcon size={16} aria-hidden="true" data-id="element-12690" data-mp="2l07qk3sev370" />
                      {t('pages.Sales.clear_all')}
                    </button>}
                </div>
                {/* Filter Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-12691" data-mp="3dpeytuho375g">
                  {/* Status Filter */}
                  <div data-id="element-12692" data-mp="38r3pdkvaaynr">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12693" data-mp="38qk3xu6fds4z">
                      {t('components.CampaignList.status')}
                    </label>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12694" data-mp="2yqtalvtlpj6h">
                      <option value="all" data-id="element-12695" data-mp="fdih0uizosf8">{t('pages.Sales.all_status')}</option>
                      <option value="completed" data-id="element-12696" data-mp="jdke0qds4xo6">
                        {t('components.getOrderStatusBadge.completed')}
                      </option>
                      <option value="pending" data-id="element-12697" data-mp="adxfcccfq5yt">
                        {t('pages.Sales.pending')}
                      </option>
                      <option value="processing" data-id="element-12698" data-mp="3ru5hxsrk10kf">
                        {t('pages.OrderDetail.processing')}
                      </option>
                      <option value="failed" data-id="element-12699" data-mp="h14w12z79rm">{t('pages.Sales.failed')}</option>
                      <option value="refunded" data-id="element-12700" data-mp="3iv0jemfzt789">
                        {t('pages.Sales.refunded')}
                      </option>
                    </select>
                  </div>
                  {/* Source Filter */}
                  <div data-id="element-12701" data-mp="38wbavk9zkz4o">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12702" data-mp="3dumm9zfxjhp0">
                      {t('pages.SaleDetail.source')}
                    </label>
                    <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12703" data-mp="2u94au9m1hlkk">
                      <option value="all" data-id="element-12704" data-mp="2za0agazcylk6">
                        {t('pages.Sales.all_sources')}
                      </option>
                      <option value="payment_link" data-id="element-12705" data-mp="396d8tm5ulnl9">
                        {t('pages.Sales.payment_link')}
                      </option>
                      <option value="qr_code" data-id="element-12706" data-mp="3mw6xa3dfy5ug">
                        {t('pages.Sales.qr_code')}
                      </option>
                      <option value="tap_to_phone" data-id="element-12707" data-mp="3i0i0g7st331u">
                        {t('pages.Sales.tap_to_phone')}
                      </option>
                      <option value="invoice" data-id="element-12708" data-mp="3rx4k4t9ebo2a">
                        {t('pages.SaleDetail.invoice')}
                      </option>
                      <option value="pos" data-id="element-12709" data-mp="1p9efmng0661j">
                        {t('pages.Sales.pos_terminal')}
                      </option>
                      <option value="recurring" data-id="element-12710" data-mp="1u6o33xju07mq">
                        {t('pages.Sales.recurring')}
                      </option>
                      <option value="virtual_terminal" data-id="element-12711" data-mp="23zde6rbgzjhj">
                        {t('pages.Sales.virtual_terminal')}
                      </option>
                    </select>
                  </div>
                  {/* Payment Method Filter */}
                  <div data-id="element-12712" data-mp="1baeq7dko1n4b">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12713" data-mp="1g9988dat77nd">
                      {t('pages.OrderDetail.payment_method')}
                    </label>
                    <select value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12714" data-mp="1l9nj033sovp3">
                      <option value="all" data-id="element-12715" data-mp="g12vro6nxiq5">
                        {t('pages.Sales.all_methods')}
                      </option>
                      <option value="card" data-id="element-12716" data-mp="b9kuez0q9g4n">{t('pages.Sales.card')}</option>
                      <option value="wallet" data-id="element-12717" data-mp="6bj6o0scwsmu">{t('pages.Sales.wallet')}</option>
                      <option value="bank_transfer" data-id="element-12718" data-mp="69f53phxk5pq">
                        {t('pages.Sales.bank_transfer')}
                      </option>
                      <option value="cash" data-id="element-12719" data-mp="3tfplhj98zz1k">{t('pages.Sales.cash')}</option>
                    </select>
                  </div>
                  {/* Date From */}
                  <div data-id="element-12720" data-mp="3jhroo7dbotxc">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12721" data-mp="3jib5fkr8lc9s">
                      {t('pages.Sales.date_from')}
                    </label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12722" data-mp="1mn5d37c67e4n" />
                  </div>
                  {/* Date To */}
                  <div data-id="element-12723" data-mp="326mg0zqzuz2h">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12724" data-mp="377af88pdmm7s">
                      {t('pages.Sales.date_to')}
                    </label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12725" data-mp="3uxwg9g0yr68a" />
                  </div>
                  {/* Amount Min */}
                  <div data-id="element-12726" data-mp="29iguwbw13824">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12727" data-mp="2eiv3bv1jkk0o">
                      {t('pages.Sales.min_amount_usd')}
                    </label>
                    <input type="number" placeholder="0" value={amountMin} onChange={e => setAmountMin(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12728" data-mp="wjf0i253yvpl" />
                  </div>
                  {/* Amount Max */}
                  <div data-id="element-12729" data-mp="8vxfqqk9gxw4">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-12730" data-mp="8wqep5czaj3p">
                      {t('pages.Sales.max_amount_usd')}
                    </label>
                    <input type="number" placeholder={t('pages.Sales.attrs.no_limit')} value={amountMax} onChange={e => setAmountMax(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-12731" data-mp="hwlujnhhb2a2" />
                  </div>
                </div>
              </div>}
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-12732" data-mp="nzveszjkl2zi">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-12733" data-mp="o2zp8enmouym">
            <thead className="bg-gray-50" data-id="element-12734" data-mp="t1ayan63n1fs">
              <tr data-id="element-12735" data-mp="xz2qzjekirl1">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12736" data-mp="xzm9p2c1lk04">
                  {t('pages.OrderDetail.transaction_id')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12737" data-mp="17o46l8h7b6yh">
                  {t('pages.Sales.date_time')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12738" data-mp="1gpc4vqjpw7sk">
                  {t('pages.Orders.customer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12739" data-mp="1lmax23jtm4o4">
                  {t('pages.SaleDetail.source')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12740" data-mp="1qro73a9hxbiw">
                  {t('pages.Sales.payment_category')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12741" data-mp="1htvxmmqggy3f">
                  {t('pages.OrderDetail.payment_method')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12742" data-mp="29ks3v4i0g4hq">
                  {t('pages.Sales.amount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12743" data-mp="20ryjisnpiex0">
                  {t('components.CampaignList.status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-12744" data-mp="2siuv7py16dip">
                  {t('pages.CampaignDetails.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-12745" data-mp="iveddm2zwsu7">
              {filteredSales.map(sale => <tr key={sale.id} onClick={() => navigate(`/sale-detail/${sale.id}`)} className="hover:bg-gray-50 cursor-pointer" data-id="element-12746" data-mp="dy5wrpxhwwv2">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12747" data-mp="16lihrvo0cwm9">
                    <div className="text-sm font-medium text-gray-900" data-id="element-12748" data-mp="11mfd4cbxwset">
                      {sale.transactionId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12749" data-mp="wtvxl9tozn7d">
                    <div className="text-sm text-gray-900" data-id="element-12750" data-mp="rwckwecn7ihj">
                      {new Date(sale.date).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-12751" data-mp="1km2ymmad4id4">
                      {new Date(sale.date).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12752" data-mp="21v5a8px4fjtr">
                    <div className="text-sm text-gray-900" data-id="element-12753" data-mp="21tu4wlo1240j">
                      {sale.customerName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-12754" data-mp="2avto9kmilk6n">
                      {sale.customerEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12755" data-mp="2ftlqp8kscop3">
                    <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-12756" data-mp="2klwni2k8budf">
                      {getSourceIcon(sale.source)}
                      {getSourceLabel(sale.source)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12757" data-mp="2ul41mrysft1l">
                    <div className="text-sm text-gray-900" data-id="element-12758" data-mp="2yl4q75flpkmd">
                      {getPaymentMethodCategory(sale.paymentMethod)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12759" data-mp="zea1zmanj4ab">
                    <div className="text-sm text-gray-900" data-id="element-12760" data-mp="uj4j6wcjq5qg">
                      {getPaymentMethodName(sale.paymentMethod)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12761" data-mp="13hznu3zxz7rn">
                    <div className="text-sm font-medium text-gray-900" data-id="element-12762" data-mp="1i75mnquvavz0">
                      {formatMoney(sale.amount, sale.currency)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-12763" data-mp="1da5ftdq71p6u">
                    {getStatusBadge(sale.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right" onClick={e => e.stopPropagation()} data-id="element-12764" data-mp="1n5pbq04rcnvl">
                    <div className="relative inline-block" data-id="element-12765" data-mp="3t10cmr0r7qgh">
                      <button aria-label="Sale actions" aria-expanded={openMenuId === sale.id} onClick={() => setOpenMenuId(openMenuId === sale.id ? null : sale.id)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-12766" data-mp="3oywxnsevm3qg">
                        <MoreVerticalIcon size={18} className="text-gray-600" aria-hidden="true" data-id="element-12767" data-mp="3a1nemflktmco" />
                      </button>

                      {/* Dropdown Menu */}
                      {openMenuId === sale.id && <>
                          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} data-id="element-12768" data-mp="2r3sy7gi4c8k9" />

                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-12769" data-mp="kbs3g93nrpvk">
                            <button onClick={() => {
                        setOpenMenuId(null);
                        navigate(`/sale-detail/${sale.id}`);
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12770" data-mp="2lg47c13uvtil">
                              <EyeIcon size={16} data-id="element-12771" data-mp="22hi5gk0ilydm" />
                              {t('pages.Sales.view_details')}
                            </button>
                            {sale.status === 'completed' && <button onClick={() => {
                        setOpenMenuId(null);
                        handleOpenRefundModal(sale);
                      }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12772" data-mp="1ss8b0xi783mc">
                                <RefreshCwIcon size={16} data-id="element-12773" data-mp="3qfkbdwd42vuc" />
                                {t('pages.SaleDetail.refund')}
                              </button>}
                          </div>
                        </>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredSales.length === 0 && <div className="text-center py-12" data-id="element-12774" data-mp="3bplctshn9iec">
            <p className="text-gray-500" data-id="element-12775" data-mp="3bqebs7ad33lx">
              {t('pages.Sales.no_transactions_found')}
            </p>
          </div>}
      </div>
      {/* Refund Modal */}
      {selectedSaleForRefund && <RefundModal isOpen={refundModalOpen} onClose={() => setRefundModalOpen(false)} transactionAmount={selectedSaleForRefund.amount} transactionId={selectedSaleForRefund.transactionId} currency={getTenant().currency.code} onRefund={handleRefund} data-id="element-12776" data-mp="3lgz6hjmwznzk" />}
    </div>;
}